import React, { useState, useRef, useEffect, useId } from 'react';
import { NodeData } from '../../types';
import { Loader2, RefreshCw, Check, AlertCircle, Sparkles } from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import { SettingChip } from './composer/Composer';
import { PROVIDER_NAMES } from './composer/modelLabels';

interface WriteNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  googleApiKey?: string;
  openAiApiKey?: string;
  isViewOnly?: boolean;
}

// Simple write/refine node that can draft text, optionally refine with Gemini, and feed connected nodes.
export const WriteNode: React.FC<WriteNodeProps> = ({ data, onDataChange, googleApiKey, openAiApiKey, isViewOnly = false }) => {
  const modelOptions = [
    { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', provider: 'google' },
    { id: 'gemini-3.0-pro', label: 'Gemini 3 Pro', provider: 'google' },
    { id: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', provider: 'google' },
    { id: 'gpt-4o-mini', label: 'GPT-4o Mini', provider: 'openai' },
    { id: 'gpt-4o', label: 'GPT-4o', provider: 'openai' },
  ] as const;

  const writeData = data.writeData || { draft: '', refined: '', modelId: modelOptions[0].id };
  const selectedModel = modelOptions.find((m) => m.id === writeData.modelId) || modelOptions[0];
  const [isRefining, setIsRefining] = useState(false);
  const [refineError, setRefineError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const draftId = useId();

  // Attach native wheel listeners that stop propagation so the canvas doesn't zoom
  useEffect(() => {
    const attachWheelTrap = (el: HTMLElement | null) => {
      if (!el) return () => {};
      const handleWheel = (e: WheelEvent) => {
        const isScrollable = el.scrollHeight > el.clientHeight;
        if (!isScrollable) return;
        const isAtTop = el.scrollTop === 0;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        const scrollingUp = e.deltaY < 0;
        const scrollingDown = e.deltaY > 0;
        if ((isAtTop && scrollingUp) || (isAtBottom && scrollingDown)) return;
        e.stopPropagation();
      };
      el.addEventListener('wheel', handleWheel, { passive: false });
      return () => el.removeEventListener('wheel', handleWheel);
    };

    const cleanupScroll = attachWheelTrap(scrollRef.current);
    const cleanupTextarea = attachWheelTrap(textareaRef.current);
    return () => { cleanupScroll(); cleanupTextarea(); };
  }, []);

  const updateWrite = (updates: Partial<typeof writeData>) => {
    onDataChange({ ...data, writeData: { ...writeData, ...updates }, timestamp: Date.now() });
  };

  const refinePrompt = async () => {
    if (!writeData.draft.trim()) return;

    const provider = selectedModel.provider;
    if (provider === 'google' && !googleApiKey) {
      setRefineError('Google API key required for this model.');
      return;
    }
    if (provider === 'openai' && !openAiApiKey) {
      setRefineError('OpenAI API key required for this model.');
      return;
    }

    setIsRefining(true);
    setRefineError('');
    const systemPrompt = 'Rewrite and enhance the user prompt. Keep a single concise, visual, specific prompt. Preserve intent. Output only the improved prompt.';

    try {
      let refined = '';
      if (provider === 'google') {
        const ai = new GoogleGenAI({ apiKey: googleApiKey });
        const response = await ai.models.generateContent({
          model: selectedModel.id,
          contents: [{ role: 'user', parts: [{ text: `${systemPrompt}\n\nUser prompt: ${writeData.draft}` }] }],
        });
        refined = response.candidates?.[0]?.content?.parts?.map((p: any) => p.text).filter(Boolean).join('').trim() || '';
      } else {
        const res = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${openAiApiKey}`,
          },
          body: JSON.stringify({
            model: selectedModel.id,
            messages: [
              { role: 'system', content: systemPrompt },
              { role: 'user', content: writeData.draft },
            ],
            temperature: 0.6,
            max_tokens: 160,
            n: 1,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error?.message || 'OpenAI request failed');
        refined = data.choices?.[0]?.message?.content?.trim() || '';
      }

      if (refined) {
        updateWrite({ refined });
      } else {
        setRefineError('No refinement returned.');
      }
    } catch (err: any) {
      console.error(err);
      setRefineError(err.message || 'Refine failed');
    } finally {
      setIsRefining(false);
    }
  };

  // The header of the card names the node, so nothing here says "Write" again. The draft takes
  // the height the card has to spare (a field in a card has no grip of its own: the card's
  // corner sizes it), and the model is a chip that opens a list, as in the Image and Video nodes.
  return (
    <div
      className="w-full h-full flex flex-col bg-white"
      style={{ touchAction: 'pan-y' }}
    >
      <div ref={scrollRef} className="flex-1 min-h-0 flex flex-col gap-3 p-3 overflow-y-auto node-scrollable-content">
        <div className="flex-1 flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <label className="ns-t-label" htmlFor={draftId}>Draft</label>
            <SettingChip
              icon={<Sparkles size={14} />}
              label="Model"
              value={selectedModel.id}
              options={modelOptions.map((m) => ({ value: m.id, label: m.label, group: PROVIDER_NAMES[m.provider] ?? m.provider }))}
              onChange={(modelId) => updateWrite({ modelId })}
              disabled={isRefining || isViewOnly}
              shrink
            />
          </div>
          <textarea
            id={draftId}
            ref={textareaRef}
            className="ns-field block flex-1 min-h-[90px] overflow-y-auto node-scrollable-content"
            placeholder="Write your prompt..."
            value={writeData.draft}
            onChange={(e) => updateWrite({ draft: e.target.value })}
            disabled={isViewOnly}
          />
        </div>

        <div className="shrink-0 flex flex-wrap items-center gap-2">
          <button
            onClick={refinePrompt}
            disabled={isRefining || !writeData.draft.trim() || isViewOnly}
            className="ns-btn ns-btn-primary ns-btn-xs"
            title={googleApiKey ? 'Refine with Gemini' : 'Add Google API key in Settings'}
          >
            {isRefining ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refine
          </button>
          {!isViewOnly && <button
            onClick={() => updateWrite({ refined: writeData.draft })}
            className="ns-btn ns-btn-soft ns-btn-xs"
          >
            Use draft
          </button>}
        </div>

        {refineError && (
          <div className="ns-note shrink-0" data-tone="danger">
            <AlertCircle />
            <span>{refineError}</span>
          </div>
        )}

        {writeData.refined && (
          <div className="shrink-0 flex flex-col gap-1.5">
            <div className="ns-t-label flex items-center gap-1">
              Refined
              <Check size={12} className="text-ok" aria-hidden="true" />
            </div>
            <div
              className="ns-well ns-t-body rounded-md px-3 py-2 whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto node-scrollable-content"
            >
              {writeData.refined}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
